import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageHeader, useToast } from 'components/admin';
import { Button, Checkbox, Input, Select } from 'components/common';
import RichTextEditor from 'components/admin/RichTextEditor';
import { createRecruitment, getAdminRecruitmentById as getRecruitmentById, updateRecruitment } from 'apis/adminRecruitments';
import { getAdminRecruitmentDepartments } from 'apis/adminRecruitmentDepartments';
import { getAdminRecruitmentJobTypes } from 'apis/adminRecruitmentJobTypes';
import { useActiveLanguagesQuery, useAdminCreateMutation, useAdminDetailQuery, useAdminListQuery, useAdminUpdateMutation } from 'hooks';
import '../../../styles/admin-shared.scss';

const RecruitmentForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const toast = useToast();
    const [errors, setErrors] = useState({});
    const [activeTab, setActiveTab] = useState(0);
    const [formData, setFormData] = useState({
        recruitmentDepartmentId: '',
        recruitmentJobTypeId: '',
        publishedAt: '',
        expiresAt: '',
        sortOrder: 0,
        isActive: true,
        allowCvSubmission: true,
        translation: [],
    });

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: departmentsData = { items: [] } } = useAdminListQuery('recruitment-departments-options', { pageSize: 1000 }, getAdminRecruitmentDepartments);
    const departments = Array.isArray(departmentsData) ? departmentsData : (departmentsData?.items || departmentsData?.data?.items || []);
    const { data: jobTypesData = { items: [] } } = useAdminListQuery('recruitment-job-types-options', { pageSize: 1000 }, getAdminRecruitmentJobTypes);
    const jobTypes = Array.isArray(jobTypesData) ? jobTypesData : (jobTypesData?.items || jobTypesData?.data?.items || []);
    const { data: detail, error: detailError } = useAdminDetailQuery('recruitments', id, getRecruitmentById);
    const createMutation = useAdminCreateMutation('recruitments', createRecruitment);
    const updateMutation = useAdminUpdateMutation('recruitments', updateRecruitment);
    const loading = createMutation.isPending || updateMutation.isPending;

    useEffect(() => {
        if (!id && languages.length) {
            setFormData(prev => ({
                ...prev,
                translation: languages.map(l => ({ languageId: l.id, title: '', slug: '', description: '' })),
            }));
        }
    }, [id, languages]);

    useEffect(() => {
        if (detail) {
            const formattedData = { ...detail };
            if (formattedData.publishedAt) formattedData.publishedAt = formattedData.publishedAt.slice(0, 16);
            if (formattedData.expiresAt) formattedData.expiresAt = formattedData.expiresAt.slice(0, 16);
            formattedData.recruitmentDepartmentId = detail.recruitmentDepartmentId ?? detail.RecruitmentDepartmentId ?? detail.department?.id ?? detail.department?.Id ?? '';
            formattedData.recruitmentJobTypeId = detail.recruitmentJobTypeId ?? detail.RecruitmentJobTypeId ?? detail.jobType?.id ?? detail.jobType?.Id ?? '';
            setFormData(formattedData);
        }
    }, [detail]);

    useEffect(() => { if (detailError) toast.error(detailError.message || 'Tải dữ liệu thất bại'); }, [detailError, toast]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleTranslationChange = (langId, field, value) => {
        setFormData(prev => ({
            ...prev,
            translation: prev.translation.map(t => t.languageId === langId ? { ...t, [field]: value } : t),
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({});
        try {
            const payload = {
                ...formData,
                recruitmentDepartmentId: formData.recruitmentDepartmentId ? Number(formData.recruitmentDepartmentId) : null,
                recruitmentJobTypeId: formData.recruitmentJobTypeId ? Number(formData.recruitmentJobTypeId) : null,
                sortOrder: Number(formData.sortOrder),
                publishedAt: formData.publishedAt ? new Date(formData.publishedAt).toISOString() : null,
                expiresAt: formData.expiresAt ? new Date(formData.expiresAt).toISOString() : null,
            };
            const res = id ? await updateMutation.mutateAsync({ id, data: payload }) : await createMutation.mutateAsync(payload);
            if (res?.success) {
                toast.success(id ? 'Cập nhật thành công' : 'Thêm mới thành công');
                navigate('/admin/recruitments');
            } else {
                if (res?.messageError) setErrors(res.messageError);
                else toast.error(res?.message || 'Lưu thất bại');
            }
        } catch (err) {
            toast.error(err.message || 'Lưu thất bại');
        }
    };

    return (
        <div className="admin-page">
            <PageHeader
                title={id ? 'Cập nhật Tin tuyển dụng' : 'Thêm Tin tuyển dụng'}
                subtitle="Quản lý tin đăng tuyển dụng với hỗ trợ đa ngôn ngữ"
            />
            <form className="form-card" onSubmit={handleSubmit}>
                <p className="form-section-title">Phân loại & Lịch trình</p>
                <div className="form-row">
                    <Select
                        label="Phòng ban tuyển dụng"
                        name="recruitmentDepartmentId"
                        value={formData.recruitmentDepartmentId || ''}
                        onChange={handleChange}
                        options={[
                            { value: '', label: '-- Chọn phòng ban --' },
                            ...departments.map(d => ({
                                value: d.id ?? d.Id,
                                label: `${(d.code || d.Code) ? `[${d.code || d.Code}] ` : ''}${d.title || d.Title || d.translations?.[0]?.title || d.RecruitmentDepartmentTranslations?.[0]?.Title || d.code || d.Code || 'Phòng ban'}`
                            }))
                        ]}
                    />
                    <Select
                        label="Loại hình làm việc"
                        name="recruitmentJobTypeId"
                        value={formData.recruitmentJobTypeId || ''}
                        onChange={handleChange}
                        options={[
                            { value: '', label: '-- Chọn loại hình --' },
                            ...jobTypes.map(jt => ({
                                value: jt.id ?? jt.Id,
                                label: `${(jt.code || jt.Code) ? `[${jt.code || jt.Code}] ` : ''}${jt.title || jt.Title || jt.translations?.[0]?.title || jt.RecruitmentJobTypeTranslations?.[0]?.Title || jt.code || jt.Code || 'Loại hình làm việc'}`
                            }))
                        ]}
                    />
                </div>
                <div className="form-row">
                    <Input
                        label="Ngày đăng"
                        name="publishedAt"
                        type="datetime-local"
                        value={formData.publishedAt}
                        onChange={handleChange}
                    />
                    <Input
                        label="Ngày hết hạn"
                        name="expiresAt"
                        type="datetime-local"
                        value={formData.expiresAt}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-row">
                    <Input
                        label="Thứ tự sắp xếp"
                        name="sortOrder"
                        type="number"
                        value={formData.sortOrder}
                        onChange={handleChange}
                    />
                </div>
                <div className="form-row" style={{ display: 'flex', gap: '24px', alignItems: 'center', marginTop: '16px' }}>
                    <Checkbox label="Hoạt động" name="isActive" checked={formData.isActive} onChange={handleChange} />
                    <Checkbox label="Cho phép nộp CV trực tuyến" name="allowCvSubmission" checked={formData.allowCvSubmission !== false} onChange={handleChange} />
                </div>

                {languages.length > 0 && (
                    <>
                        <p className="form-section-title">Bản dịch</p>
                        <div className="translation-tabs">
                            <div className="tab-header">
                                {languages.map((lang, i) => (
                                    <button
                                        key={lang.id}
                                        type="button"
                                        className={`tab-btn ${activeTab === i ? 'active' : ''}`}
                                        onClick={() => setActiveTab(i)}
                                    >
                                        {lang.name} ({lang.code})
                                    </button>
                                ))}
                            </div>
                            {languages.map((lang, i) => {
                                const t = formData.translation.find(t => t.languageId === lang.id) || {};
                                return activeTab === i ? (
                                    <div key={lang.id} className="tab-content">
                                        <Input
                                            label="Tiêu đề *"
                                            value={t.title || ''}
                                            onChange={e => handleTranslationChange(lang.id, 'title', e.target.value)}
                                            required
                                            error={errors[`translation.${lang.id}.title`]}
                                        />
                                        <Input
                                            label="Slug"
                                            value={t.slug || ''}
                                            onChange={e => handleTranslationChange(lang.id, 'slug', e.target.value)}
                                        />
                                        <div className="input-group">
                                            <label>Mô tả</label>
                                            <RichTextEditor value={t.description || ''} onChange={value => handleTranslationChange(lang.id, 'description', value)} placeholder="Nhập mô tả..." />
                                        </div>
                                    </div>
                                ) : null;
                            })}
                        </div>
                    </>
                )}

                <div className="form-actions">
                    <Button type="submit" disabled={loading}>
                        {loading ? 'Đang lưu...' : id ? 'Cập nhật' : 'Thêm mới'}
                    </Button>
                    <Button type="button" variant="secondary" onClick={() => navigate('/admin/recruitments')}>
                        Hủy
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default RecruitmentForm;
